// src/pages/organization/team/tabs/PerformanceTab.js
import React, { useState, useEffect, useCallback } from "react";
import DeleteConfirmationDialog from "../../../../components/common/DeleteConfirmationDialog";
const APP_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const PerformanceTab = ({ playerId, team }) => {
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [selectedPerformance, setSelectedPerformance] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const itemsPerPage = 5;

  const fetchPerformances = useCallback(async () => {
    try {
      const response = await fetch(
        `https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/performance/get-all-performances-of-a-player/?playerId=${playerId}&page=${currentPage}&limit=${itemsPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch performances");
      const data = await response.json();
      setPerformances(data.performances);
      setTotalPages(Math.ceil(data.total / itemsPerPage));
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [playerId, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchPerformances();
  }, [playerId, currentPage, fetchPerformances]);

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;

    const formData = new FormData();
    formData.append("performanceDocument", uploadFile);

    try {
      const response = await fetch(
        `${
          "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1"
        }/performance/create-performance/?teamId=${
          team._id
        }&playerId=${playerId}&date=${new Date().toISOString()}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload performance");
      }

      setIsUploadModalOpen(false);
      setUploadFile(null);
      fetchPerformances();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeletePerformance = async () => {
    try {
      const response = await fetch(
        `https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/performance/delete-performance/?performanceId=${selectedPerformance._id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete performance");
      fetchPerformances();
      setIsDeletePopupOpen(false);
    } catch (error) {
      setError(error.message);
    }
  };

  if (loading) return <div>Loading performances...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Performance Records</h2>
        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F15213] hover:bg-[#D4420F]"
        >
          Upload Performance
        </button>
      </div>

      <div className="space-y-4">
        {performances.map((performance) => (
          <div key={performance.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium">{performance.title}</h3>
                <p className="text-sm text-gray-500">
                  Created by: {performance.createdByName} on{" "}
                  {new Date(performance.createdAt).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedPerformance(performance);
                  setIsDeletePopupOpen(true);
                }}
                className="text-red-600 hover:text-red-800"
              >
                Delete
              </button>
            </div>
            <div className="mt-2">
              <a
                href={`${APP_URL}${performance.performanceDocument.replace(
                  "/app",
                  ""
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
              >
                Download Performance
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          <button
            onClick={() =>
              currentPage > 1 && setCurrentPage((prev) => prev - 1)
            }
            disabled={currentPage <= 1 || loading}
            className="px-4 py-2 border rounded-md transition-colors duration-200 
                        disabled:opacity-50 disabled:cursor-not-allowed
                        enabled:hover:bg-gray-100 enabled:active:bg-gray-200"
            aria-label="Previous page"
          >
            Previous
          </button>

          <div className="flex items-center px-4">
            {totalPages > 0 ? (
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
            ) : (
              <span className="text-sm text-gray-500">No pages</span>
            )}
          </div>

          <button
            onClick={() =>
              currentPage < totalPages && setCurrentPage((prev) => prev + 1)
            }
            disabled={currentPage >= totalPages || loading}
            className="px-4 py-2 border rounded-md transition-colors duration-200 
                        disabled:opacity-50 disabled:cursor-not-allowed
                        enabled:hover:bg-gray-100 enabled:active:bg-gray-200"
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      )}
      {totalPages <= 0 && (
        <div className="flex justify-center mt-4 space-x-2">
          <span className="text-xl text-gray-500">No Performance Records</span>
        </div>
      )}

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Upload Performance Record
            </h2>

            <form onSubmit={handleFileUpload} className="space-y-6">
              <div className="form-group">
                <label
                  htmlFor="performanceFile"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Upload Document
                </label>
                <div
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 
                                        ${
                                          uploadFile
                                            ? "border-green-500 bg-green-50"
                                            : "border-gray-300"
                                        } 
                                        border-dashed rounded-md hover:border-blue-500 transition-colors cursor-pointer`}
                  onClick={() =>
                    document.getElementById("performanceFile").click()
                  }
                >
                  <div className="space-y-1 text-center">
                    {uploadFile ? (
                      <>
                        <svg
                          className="mx-auto h-12 w-12 text-green-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="text-sm text-gray-600">
                          {uploadFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUploadFile(null);
                          }}
                          className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium focus:outline-none"
                        >
                          Remove file
                        </button>
                      </>
                    ) : (
                      <>
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-gray-600 justify-center">
                          <label
                            htmlFor="performanceFile"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                          >
                            <span>Choose a file</span>
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PDF, PNG, JPG up to 10MB
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <input
                  id="performanceFile"
                  type="file"
                  className="sr-only"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  accept=".pdf,.png,.jpg,.jpeg"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsUploadModalOpen(false);
                    setUploadFile(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!uploadFile}
                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 
                                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                                        transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmationDialog
        isOpen={isDeletePopupOpen}
        onClose={() => setIsDeletePopupOpen(false)}
        onConfirm={handleDeletePerformance}
        title="Delete Performance Record"
        message="Are you sure you want to delete this performance record?"
        confirmButtonText="Delete"
      />
    </div>
  );
};

export default PerformanceTab;
